import React, { useState, useEffect, useCallback } from 'react';
import { getLogs, getLogDetails, startPollingLog, getPollingLog } from '../api';
import './LogsPage.css';

function LogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Пагинация
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const perPage = 20;
  
  // Фильтры
  const [days, setDays] = useState(7);
  const [uidFilter, setUidFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  
  // Модальное окно деталей
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [logDetails, setLogDetails] = useState(null);

  // Логирование поллинга
  const [pollingLogActive, setPollingLogActive] = useState(false);
  const [pollingLogSeconds, setPollingLogSeconds] = useState(0);
  const [pollingLogEntries, setPollingLogEntries] = useState([]);
  const [pollingLogModalOpen, setPollingLogModalOpen] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const params = {
        page,
        per_page: perPage,
        days
      };
      
      if (uidFilter.trim()) {
        params.uid = uidFilter.trim();
      }
      if (typeFilter.trim()) {
        params.type = typeFilter.trim();
      }
      
      const result = await getLogs(params);
      setLogs(result.logs || []);
      setTotalPages(result.pages || 1);
      setTotal(result.total || 0);
    } catch (err) {
      setError(err.message || 'Ошибка загрузки логов');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [page, days, uidFilter, typeFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Сброс страницы при изменении фильтров
  useEffect(() => {
    setPage(1);
  }, [days, uidFilter, typeFilter]);

  // Таймер обратного отсчёта для логирования поллинга
  useEffect(() => {
    if (!pollingLogActive) return;
    const interval = setInterval(() => {
      setPollingLogSeconds(prev => {
        if (prev <= 1) {
          setPollingLogActive(false);
          // Забираем результат
          getPollingLog().then(data => {
            setPollingLogEntries(data.entries || []);
            setPollingLogModalOpen(true);
          }).catch(() => {});
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [pollingLogActive]);

  const handleStartPollingLog = async () => {
    try {
      await startPollingLog(60);
      setPollingLogActive(true);
      setPollingLogSeconds(60);
      setPollingLogEntries([]);
    } catch (err) {
      setError('Ошибка запуска логирования поллинга: ' + (err.message || ''));
    }
  };

  const handleStopAndShowPollingLog = async () => {
    try {
      const data = await getPollingLog();
      setPollingLogEntries(data.entries || []);
      setPollingLogModalOpen(true);
      setPollingLogActive(false);
      setPollingLogSeconds(0);
    } catch (err) {
      setError('Ошибка получения лога поллинга: ' + (err.message || ''));
    }
  };

  const handleRowClick = async (log) => {
    setSelectedLog(log);
    setDetailsLoading(true);
    setLogDetails(null);
    
    try {
      const details = await getLogDetails(log.log_id);
      setLogDetails(details);
    } catch (err) {
      setLogDetails({ error: err.message || 'Ошибка загрузки деталей' });
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeModal = () => {
    setSelectedLog(null);
    setLogDetails(null);
  };

  const formatDate = (isoString) => {
    if (!isoString) return '—';
    const date = new Date(isoString);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'finished': return 'status-success';
      case 'error': return 'status-error';
      case 'started': return 'status-pending';
      default: return 'status-unknown';
    }
  };

  return (
    <div className="logs-page">
      <div className="page-header">
        <h1>Логи запросов</h1>
        <div className="page-header-actions">
          {pollingLogActive ? (
            <button className="secondary" onClick={handleStopAndShowPollingLog}>
              ⏱ Поллинг лог ({pollingLogSeconds}с)
            </button>
          ) : (
            <button className="secondary" onClick={handleStartPollingLog}>
              📡 Логгировать поллинг 1 мин
            </button>
          )}
          <button className="secondary" onClick={fetchLogs} disabled={loading}>
            Обновить
          </button>
        </div>
      </div>

      {/* Фильтры */}
      <div className="filters-bar">
        <div className="filter-group">
          <label>Период:</label>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={1}>1 день</option>
            <option value={7}>7 дней</option>
            <option value={14}>14 дней</option>
            <option value={30}>30 дней</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label>UID:</label>
          <input
            type="text"
            placeholder="Фильтр по UID"
            value={uidFilter}
            onChange={(e) => setUidFilter(e.target.value)}
          />
        </div>
        
        <div className="filter-group">
          <label>Тип:</label>
          <input
            type="text"
            placeholder="Тип запроса"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          />
        </div>
        
        <div className="filter-info">
          Всего: {total}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Загрузка логов</div>
      ) : logs.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <p>Логи не найдены</p>
          </div>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="logs-table">
              <thead>
                <tr>
                  <th>Дата/Время</th>
                  <th>UID</th>
                  <th>Тип запроса</th>
                  <th>ID запроса</th>
                  <th>Статус</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.log_id} onClick={() => handleRowClick(log)} className="clickable">
                    <td className="col-date">{formatDate(log.timestamp)}</td>
                    <td className="col-uid">{log.uid || '—'}</td>
                    <td className="col-type">{log.request_type || '—'}</td>
                    <td className="col-request-id">{log.request_id || '—'}</td>
                    <td className="col-status">
                      <span className={`status-badge ${getStatusClass(log.status)}`}>
                        {log.status || 'unknown'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Пагинация */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="secondary"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                ← Назад
              </button>
              <span className="pagination-info">
                Страница {page} из {totalPages}
              </span>
              <button
                className="secondary"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                Вперёд →
              </button>
            </div>
          )}
        </>
      )}

      {/* Модальное окно деталей */}
      {selectedLog && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Детали лога</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            
            {detailsLoading ? (
              <div className="loading">Загрузка деталей</div>
            ) : logDetails?.error ? (
              <div className="error-message">{logDetails.error}</div>
            ) : logDetails ? (
              <div className="modal-body">
                <div className="detail-row">
                  <span className="detail-label">ID лога:</span>
                  <span className="detail-value mono">{logDetails.log_id}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Время:</span>
                  <span className="detail-value">{formatDate(logDetails.timestamp)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">UID устройства:</span>
                  <span className="detail-value">{logDetails.uid || '—'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Тип запроса:</span>
                  <span className="detail-value">{logDetails.request_type || '—'}</span>
                </div>
                
                <div className="detail-section">
                  <h3>Запрос</h3>
                  <pre className="code-block">
                    {JSON.stringify(logDetails.request, null, 2)}
                  </pre>
                </div>
                
                <div className="detail-section">
                  <h3>Ответ сервера</h3>
                  <pre className="code-block">
                    {JSON.stringify(logDetails.response, null, 2)}
                  </pre>
                </div>
                
                {logDetails.ai_result && (
                  <div className="detail-section">
                    <h3>Результат AI</h3>
                    <pre className="code-block ai-result">
                      {logDetails.ai_result}
                    </pre>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Модальное окно лога поллинга */}
      {pollingLogModalOpen && (
        <div className="modal-overlay" onClick={() => setPollingLogModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Лог transcribe-poll ({pollingLogEntries.length} записей)</h2>
              <button className="modal-close" onClick={() => setPollingLogModalOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              {pollingLogEntries.length === 0 ? (
                <div className="empty-state">
                  <p>За период не было ни одного transcribe-poll запроса</p>
                </div>
              ) : (
                <div className="table-container">
                  <table className="logs-table">
                    <thead>
                      <tr>
                        <th>Время</th>
                        <th>UID устройства</th>
                        <th>Результат</th>
                        <th>Текст</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pollingLogEntries.map((entry, idx) => (
                        <tr key={idx}>
                          <td>{entry.timestamp || '—'}</td>
                          <td>{entry.device_uid || '—'}</td>
                          <td>
                            <span className={`status-badge ${entry.result === 'ok' ? 'status-success' : entry.result === 'empty' ? 'status-unknown' : 'status-error'}`}>
                              {entry.result || '—'}
                            </span>
                          </td>
                          <td style={{maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                            {entry.text || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LogsPage;